import React from 'react';
import POSTable, { RowType } from '@/components/POS/Common/POSTable';
import Permission from '@/utils/POS/Permission';
import { t } from 'i18next';
import type { UnitsListRow } from '@/hooks/api/pos/units/useUnits';
import { IconButton, Stack } from '@mui/material';
import moment from 'moment';

const DeleteIcon: string = require('@/assets/Delete.svg').default;
const EditIcon: string = require('@/assets/editProduct.svg').default;

export default function ProductUnitsList({
    units,
    loading,
    onEdit,
    onDelete,
    onRowClick,
}: {
    units?: UnitsListRow[];
    loading?: boolean;
    onEdit: (unit: UnitsListRow) => void;
    onDelete: (unit: UnitsListRow) => void;
    onRowClick?: (unit: UnitsListRow) => void;
}) {
    const { isAllowed } = Permission();
    const canEdit = isAllowed('Unit', 'update');
    const canDelete = isAllowed('Unit', 'delete');

    const columns = [
        {
            id: 'name',
            name: t('Common.Name'),
            selector: (row: RowType) => row.name,
            sortable: true,
        },
        {
            id: 'abbreviation',
            name: t('Common.Abbreviation'),
            selector: (row: RowType) => row.abbreviation,
            sortable: true,
        },
        {
            id: 'createdAt',
            name: t('Doctors.CreatedAt'),
            selector: (row: RowType) => moment(row.createdAt).format('DD/MM-YYYY HH:mm'),
            sortable: false,
        },
        {
            id: 'unitOrigin',
            name: '',
            selector: (row: RowType) => {
                const unit = row as UnitsListRow;
                return unit.isDefault === true ? '' : t('POS.CustomUnit');
            },
            sortable: false,
        },
        {
            id: 'actions',
            name: '',
            selector: (row: RowType) => {
                const unit = row as UnitsListRow;
                const isDefaultUnit = unit.isDefault === true;

                return (
                    <Stack direction="row" justifyContent="center" alignItems="center" sx={{ gap: 1, minWidth: 90 }}>
                        {canEdit && !isDefaultUnit && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(unit);
                                }}
                                sx={{ minWidth: 'auto' }}
                            >
                                <img src={EditIcon} alt="" style={{ width: 18, height: 18 }} />
                            </IconButton>
                        )}
                        {canDelete && !isDefaultUnit && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(unit);
                                }}
                                sx={{ minWidth: 'auto' }}
                            >
                                <img src={DeleteIcon} alt="" style={{ width: 18, height: 18 }} />
                            </IconButton>
                        )}
                    </Stack>
                );
            },
            sortable: false,
        },
    ];

    return (
        <POSTable
            columns={columns}
            data={units ?? []}
            loading={loading}
            isServerSorting={false}
            defaultOrder="name"
            onRowClick={(row) => {
                const unit = row as UnitsListRow;
                if (!canEdit || unit.isDefault === true) return;
                onRowClick?.(unit);
            }}
        />
    );
}
